import {useCallback, useContext, useEffect, useRef, useState} from "react";
import FilePreview from "./FilePreview";
import ContextMenu, {ContextMenuItem} from "./ContextMenu";

import './VideoList.scss'
import {videoSortMethods, VideoSortMethodType} from "../lib/videoSortMethods";
import AppContext from "../context/AppContext";
import {getVideoLink} from "../lib/static";
import BulkAddTag from "./bulk-actions/BulkAddTag";
import BulkRemoveTag from "./bulk-actions/BulkRemoveTag";
import BulkRename from "./bulk-actions/BulkRename";
import {useLocation, useNavigate} from "react-router-dom";
import {useApi} from "../api";

type Props = {
    loading: boolean
    files: VideoFile[]
    onLoad?: () => void
    showSort?: boolean
    showDirectory?: boolean
    canBeSelected?: boolean
}

export default ({ loading, files, onLoad, showSort = true, showDirectory = false, canBeSelected = false }: Props) => {
    const location = useLocation()

    const api = useApi()

    const navigate = useNavigate()

    const {
        setSelectedVideo, reloadView, sortType, setSortType, limitToFileType, setLimitToFileType,
        fetchingAdditionalFiles, maybeFetchAdditionalFiles, hasMoreFiles, hasFileManager,
        setQueue,
    } = useContext(AppContext)

    const [ selectedVideos, setSelectedVideos ] = useState<VideoFile[]>([])

    const [ withoutTags, setWithoutTags ] = useState<boolean>(localStorage.getItem('only-without-tags') === '1')
    const [ onlyFavourites, setOnlyFavourites ] = useState<boolean>(localStorage.getItem('only-favourite') === '1')

    const [ bulkAction, setBulkAction ] = useState<string>('')

    const [ ctrlPressed, setCtrlPressed ] = useState<boolean>(false)
    const [ selectionMode, setSelectionMode ] = useState<boolean>(false)

    const [ lastSelectAction, setLastSelectAction ] = useState<{ selected: boolean, video: VideoFile }|null>(null)
    const [ shiftPressed, setShiftPressed ] = useState<boolean>(false)

    type CtxMenu = { x: number, y: number, file: VideoFile }
    const [ contextMenu, setContextMenu ] = useState<CtxMenu | null>(null)

    const selectedVideosRef = useRef<VideoFile[]>([])
    selectedVideosRef.current = selectedVideos

    const loadMoreRef = useRef<HTMLDivElement>(null)

    const onSortChange = (e: any) => {
        setSortType(e.target.value as VideoSortMethodType)
        localStorage.setItem('video-sort-type', e.target.value)
    }

    const classNames = ['file-list']
    if (loading) {
        classNames.push('loading')
    }

    useEffect(() => {
        if (bulkAction === 'clear-index') {
            setBulkAction('')
            ;(async () => {
                if (window.confirm('Are you sure?')) {
                    await api.removeFilesIndex(selectedVideos.map(v => v.hash))
                    reloadView(undefined)
                }
            })()
        }
        if (bulkAction === 'remove-files') {
            setBulkAction('')
            ;(async () => {
                if (window.confirm('Are you sure?')) {
                    await api.removeFiles(selectedVideos.map(v => v.hash))
                    reloadView(undefined)
                }
            })()
        }
        if (bulkAction === 'export') {
            const ids = selectedVideosRef.current.map(v => v.hash)
            setBulkAction('')
            api.exportFiles(ids, `export-${ids.length}-files.zip`)
        }
    }, [bulkAction]);

    const setVideoSelectionMultiple = (videos: VideoFile[], selected: boolean) => {
        if (selected) {
            setSelectedVideos([ ...selectedVideosRef.current, ...videos ])
        } else {
            const ids = videos.map(v => v.hash)
            setSelectedVideos(selectedVideosRef.current.filter(v => !ids.includes(v.hash)))
        }

        const last = videos[videos.length - 1]
        if (last) {
            setLastSelectAction({ selected, video: last })
        }
    }

    const setVideoSelection = (video: VideoFile, selected: boolean) => {
        setVideoSelectionMultiple([video], selected)
    }

    const toggleVideoSelection = (video: VideoFile) => {
        if (selectedVideos.includes(video)) {
            setVideoSelection(video, false)
        } else {
            setVideoSelection(video, true)
        }
    }

    const onClick = (video: VideoFile) => {
        if (selectionMode) {
            toggleVideoSelection(video)
            return
        }
        if (canBeSelected && ctrlPressed) {
            toggleVideoSelection(video)
            return
        }
        if (canBeSelected && shiftPressed) {
            if (!lastSelectAction) {
                toggleVideoSelection(video)
                return
            }
            let indexLast: number|null = null
            let indexNow: number|null = null

            for (const index in files) {
                if (files[index] === lastSelectAction.video) {
                    indexLast = parseInt(index)
                    continue
                }
                if (files[index] === video) {
                    indexNow = parseInt(index)
                }
                if (indexLast !== null && indexNow != null) {
                    break
                }
            }

            if (indexLast !== null && indexNow !== null) {
                let shouldBeSelected = lastSelectAction.selected
                let filesToChange: VideoFile[] = []

                if (indexLast < indexNow) {
                    for (let i = indexLast; i <= indexNow; i++) {
                        const file = files[i]
                        if (typeof file !== 'undefined') {
                            filesToChange.push(file)
                        } else {
                            console.log('no file with index', i)
                        }
                    }
                } else {
                    for (let i = indexLast; i >= indexNow; i--) {
                        const file = files[i]
                        if (typeof file !== 'undefined') {
                            filesToChange.push(file)
                        } else {
                            console.log('no file with index', i)
                        }
                    }
                }

                setVideoSelectionMultiple(filesToChange, shouldBeSelected)
            }

            console.log({ indexLast, indexNow })
            return
        }

        setSelectedVideo(video)
        window.history.pushState(null, '', getVideoLink(video.hash))
    }

    const onVideoSelect = (video: VideoFile, selected: boolean) => {
        toggleVideoSelection(video)
    }

    const getClassName = (video: VideoFile) => {
        if (selectedVideos.length > 0 && !selectedVideos.includes(video)) {
            return 'not-selected'
        }
        return ''
    }

    const toggleAll = (e: any) => {
        e.preventDefault()

        const select = !selectedVideos.map(v => v.hash).includes(files[0].hash)

        if (select) {
            setSelectedVideos([ ...files ])
        } else {
            setSelectedVideos([])
        }
    }

    const tagsAdded = (tags: string[]) => {
        reloadView(undefined)
    }

    const tagsRemoved = (tags: string[]) => {
        reloadView(undefined)
    }

    useEffect(() => {
        if (onLoad) {
            onLoad()
        }

        if (canBeSelected) {
            const onKeyDown = (e: any) => {
                if (e.key === 'Control') {
                    setCtrlPressed(true)
                }
                if (e.key === 'Shift') {
                    setShiftPressed(true)
                }
            }
            const onKeyUp = (e: any) => {
                if (e.key === 'Control') {
                    setCtrlPressed(false)
                }
                if (e.key === 'Shift') {
                    setShiftPressed(false)
                }
            }
            const onBlur = () => {
                setCtrlPressed(false)
                setShiftPressed(false)
            }

            window.addEventListener('keydown', onKeyDown)
            window.addEventListener('keyup', onKeyUp)
            window.addEventListener('blur', onBlur)

            return () => {
                window.removeEventListener('keydown', onKeyDown)
                window.removeEventListener('keyup', onKeyUp)
                window.removeEventListener('blur', onBlur)
            }
        }
    }, []);

    useEffect(() => {
        setSelectedVideos([])
        setSelectionMode(false)
    }, [ files ]);

    useEffect(() => {
        if (selectionMode && selectedVideos.length === 0) {
            setSelectionMode(false)
        }
    }, [selectedVideos, selectionMode])

    const buildContextMenuItems = useCallback((file: VideoFile): ContextMenuItem[] => {
        const items: ContextMenuItem[] = []

        if (canBeSelected) {
            items.push({
                label: 'Select',
                onClick: () => {
                    setSelectionMode(true)
                    setVideoSelection(file, true)
                },
            })
        }

        if (file.directory?.hash && location.pathname !== `/dir/${file.directory.hash}`) {
            items.push({
                label: 'Go to folder',
                onClick: () => {
                    setSelectedVideo(null)
                    navigate(`/dir/${file.directory.hash}`)
                },
            })
        }

        if (file.fileType === 'video' || file.fileType === 'photo') {
            items.push({
                label: 'Play from here',
                onClick: () => {
                    const visible = visibleFilesRef.current.filter(f => f.fileType === 'video' || f.fileType === 'photo')
                    const idx = visible.findIndex(f => f.hash === file.hash)
                    setQueue(visible, Math.max(0, idx))
                },
            })
        }

        items.push({
            label: 'Remove from history',
            onClick: () => {
                api.setWatchPosition(file.hash, 0).then(() => {
                    window.dispatchEvent(new CustomEvent('memo:history-changed'))
                })
            },
        })

        if (hasFileManager) {
            items.push({
                label: 'Open in file manager',
                onClick: () => api.openInFileManager(file.hash),
            })
        }

        items.push({
            label: 'Delete file',
            danger: true,
            onClick: () => {
                if (window.confirm('Delete this file from disk?')) {
                    api.removeFiles([file.hash]).then(() => reloadView(undefined))
                }
            },
        })

        items.push({
            label: 'Remove index',
            danger: true,
            onClick: () => {
                if (window.confirm('Remove index for this file?')) {
                    api.removeFilesIndex([file.hash]).then(() => reloadView(undefined))
                }
            },
        })

        return items
    }, [canBeSelected, hasFileManager, api, navigate, location.pathname, reloadView, setSelectedVideo, setQueue])

    useEffect(() => {
        localStorage.setItem('only-without-tags', withoutTags ? '1' : '0')
    }, [ withoutTags ]);

    useEffect(() => {
        localStorage.setItem('only-favourite', onlyFavourites ? '1' : '0')
    }, [ onlyFavourites ]);

    const scrollCallbackRef = useRef({ hasMoreFiles, maybeFetchAdditionalFiles })
    useEffect(() => {
        scrollCallbackRef.current = { hasMoreFiles, maybeFetchAdditionalFiles }
    })

    useEffect(() => {
        const check = () => {
            if (loadMoreRef.current) {
                const scroll = window.innerHeight + window.scrollY
                const refTop = loadMoreRef.current.offsetTop

                if (scrollCallbackRef.current.hasMoreFiles() && scroll >= refTop - 800) {
                    scrollCallbackRef.current.maybeFetchAdditionalFiles()
                }
            }
        }

        window.addEventListener('scroll', check)

        return () => {
            window.removeEventListener('scroll', check)
        }
    }, []);

    const changeFileType = (e: any, fileType: string | null) => {
        e.preventDefault()
        setLimitToFileType(fileType)
        localStorage.setItem('file-type', fileType || '')
    }

    const isDir = location.pathname === '/' || location.pathname.startsWith('/dir/')

    const defaultFilter = (file: VideoFile) => {
        if (limitToFileType && file.fileType !== limitToFileType) {
            return false
        }
        if (isDir && withoutTags && file.tags && file.tags.length > 0) {
            return false
        }
        if (isDir && onlyFavourites && !file.isFavourite) {
            return false
        }

        return true
    }

    const filtered = files?.filter(defaultFilter) || []
    const visibleFilesRef = useRef<VideoFile[]>([])
    visibleFilesRef.current = filtered

    return (
        <div className="video-list">
            {files?.length > 0 && showSort && (
                <>
                    <div className="vl-toolbar">
                        <button className={'vl-filter-chip' + (limitToFileType == null    ? ' active' : '')} onClick={e => changeFileType(e, null)}>All</button>
                        <button className={'vl-filter-chip' + (limitToFileType === 'video' ? ' active' : '')} onClick={e => changeFileType(e, 'video')}>Video</button>
                        <button className={'vl-filter-chip' + (limitToFileType === 'photo' ? ' active' : '')} onClick={e => changeFileType(e, 'photo')}>Photo</button>

                        {isDir && (
                            <>
                                <button className={'vl-filter-chip' + (onlyFavourites ? ' active' : '')} onClick={() => setOnlyFavourites(!onlyFavourites)}>♥ Favourites</button>
                                <button className={'vl-filter-chip' + (withoutTags    ? ' active' : '')} onClick={() => setWithoutTags(!withoutTags)}>Untagged</button>
                            </>
                        )}

                        <select className="vl-sort" value={sortType} onChange={onSortChange} disabled={loading}>
                            {Object.keys(videoSortMethods).map(type => (
                                <option key={type} value={type}>{videoSortMethods[type].name}</option>
                            ))}
                        </select>

                        {filtered.length > 1 && (
                            <>
                                <button
                                    className="vl-queue-btn"
                                    title="Play all"
                                    onClick={() => setQueue(filtered.filter(f => f.fileType === 'video' || f.fileType === 'photo'), 0)}
                                >▶ Play all</button>
                                <button
                                    className="vl-queue-btn"
                                    title="Shuffle all"
                                    onClick={() => {
                                        const playable = filtered.filter(f => f.fileType === 'video' || f.fileType === 'photo')
                                        const shuffled = [...playable].sort(() => Math.random() - 0.5)
                                        setQueue(shuffled, 0)
                                    }}
                                >⇌ Shuffle</button>
                            </>
                        )}
                    </div>

                    {canBeSelected && (
                        <div className="vl-bulk-menu">
                            <button onClick={toggleAll}>
                                {selectedVideos.filter(defaultFilter).length ? 'Deselect all' : 'Select all'}
                            </button>

                            {selectedVideos.filter(defaultFilter).length > 0 && (
                                <>
                                    <span>{selectedVideos.filter(defaultFilter).length} selected</span>
                                    <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
                                        <option value="">Bulk action…</option>
                                        <option value="add-tag">Add tags</option>
                                        <option value="remove-tag">Remove tag</option>
                                        <option value="rename">Rename</option>
                                        <option value="export">Export as zip</option>
                                        <option value="clear-index">Clear index</option>
                                        <option value="remove-files">Delete files</option>
                                    </select>
                                </>
                            )}

                            {bulkAction === 'add-tag' && selectedVideos.filter(defaultFilter).length ? (
                                <BulkAddTag videoIds={selectedVideos.filter(defaultFilter).map(v => v.hash)} onChanged={tagsAdded} onClose={() => setBulkAction('')}/>
                            ) : null}
                            {bulkAction === 'remove-tag' && selectedVideos.filter(defaultFilter).length ? (
                                <BulkRemoveTag videos={selectedVideos.filter(defaultFilter)} onChanged={tagsRemoved} onClose={() => setBulkAction('')}/>
                            ) : null}
                            {bulkAction === 'rename' && selectedVideos.filter(defaultFilter).length ? (
                                <BulkRename
                                    files={selectedVideos.filter(defaultFilter)}
                                    onClose={() => setBulkAction('')}
                                    onRenamed={() => { setSelectedVideos([]); onLoad?.() }}
                                />
                            ) : null}
                        </div>
                    )}
                </>
            )}

            {files?.length > 0 && (
                <ul className={classNames.join(' ')}>
                    {files.map(file => (
                        <li key={file.hash} style={{ display: !defaultFilter(file) ? 'none' : 'block' }}>
                            <FilePreview
                                file={file}
                                showDirectory={showDirectory}
                                className={getClassName(file)}
                                canBeSelected={canBeSelected}
                                selected={selectedVideos.includes(file)}
                                selectionMode={selectionMode}
                                onSelect={(selected: boolean) => onVideoSelect(file, selected)}
                                onClick={() => onClick(file)}
                                onContextMenu={(x, y) => setContextMenu({ x, y, file })}
                            />
                        </li>
                    ))}
                </ul>
            )}

            <div ref={loadMoreRef}/>

            {fetchingAdditionalFiles && (
                <div className="vl-load-more">
                    <span className="vl-spin"/>
                    Loading more…
                </div>
            )}

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={buildContextMenuItems(contextMenu.file)}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    )
}
